import { ChevronRight, Play, RotateCcw, Square } from "lucide-react";
import { useState } from "react";
import { useApp } from "@/lib/AppContext";
import { ApiError, type PipelineRun, type PipelineStep } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatElapsed } from "@/lib/format";
import { useStepArtifacts } from "@/lib/useStepArtifacts";
import type { PipelineRunState } from "@/lib/usePipelineRun";
import { StepDetails } from "@/pages/StepDetail";
import { RestartControl } from "@/pages/RestartControl";

/**
 * What the pipeline has done, or not done, to one source (ARCH-2).
 *
 * Deliberately small, and here rather than on a screen of its own. The recordings list
 * with live status is UI-4 and the per-step debug view is ARCH-8; this is the step-level
 * view of one source, and it is also the only way to start a run for a recording that
 * arrived before there was a pipeline — without one it would sit there forever while
 * the worker polls an empty queue.
 *
 * The run itself is fetched by the caller (`usePipelineRun`) rather than here, because
 * from M3.1 the recording detail needs the same answer at the same moment to decide
 * whether to show this panel at all. Two owners of one poll would be two timers and,
 * briefly, two opinions.
 */
export function PipelinePanel({
  sourceId,
  kind,
  state,
}: {
  sourceId: string;
  kind: string;
  state: PipelineRunState;
}) {
  const { api, refreshRecordings, refreshSetup, settings } = useApp();
  const { run, adopt } = state;

  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Two ways this can go wrong and they are reported in one place: the run could not be
  // read (the caller's poll), or starting one failed (here).
  const [startProblem, setProblem] = useState<string | null>(null);
  const problem = startProblem ?? state.problem;

  const developerMode = settings?.developerMode ?? false;

  // The whole run's elapsed time, live while it is still going: nothing has happened
  // yet for a run still `queued`, so there is nothing honest to show until a step has
  // actually started.
  const runElapsedMs =
    run && run.status !== "queued"
      ? (run.completedAt ? Date.parse(run.completedAt) : Date.now()) - Date.parse(run.createdAt)
      : null;

  async function start() {
    setStarting(true);
    setProblem(null);

    try {
      adopt(await api.startPipeline(sourceId));
      // The recordings list is showing this one as "Not started"; it should not still
      // be saying so a poll later.
      refreshRecordings();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The pipeline could not be started.");
    } finally {
      setStarting(false);
    }
  }

  /**
   * SPCH-9 — stop at the current step boundary.
   *
   * The step already running is left alone and finishes; nothing after it is handed out.
   * That is the correct semantics rather than a shortcut — a lease abandoned mid-step is
   * indistinguishable from a machine losing power, and burning an attempt on a
   * deliberate cancel is worse than waiting for one step. The panel says "Cancelling"
   * until that last step lands, because it would otherwise look as though nothing had
   * happened for as long as an hour of transcription takes to finish.
   */
  async function cancel() {
    setStopping(true);
    setProblem(null);

    try {
      adopt(await api.cancelPipeline(sourceId));
      refreshRecordings();
      refreshSetup();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The pipeline could not be cancelled.");
    } finally {
      setStopping(false);
    }
  }

  if (run === undefined) return null;

  return (
    <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex flex-wrap items-start justify-between gap-[var(--space-4)]">
        <div className="min-w-0">
          <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">Processing</h2>
          <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {Describe(run, kind)}
            {runElapsedMs !== null && runElapsedMs >= 0 && ` · ${formatElapsed(runElapsedMs)} so far`}
          </p>
        </div>

        <div className="flex flex-wrap items-start gap-[var(--space-3)]">
          {/* A run that never existed, and a run that stopped — the two states with
              nowhere else to go. Restarting at a *step* is SPCH-10 and keeps what the
              earlier steps produced; this starts the chain again from the top, which is
              all there is until then. Without it a failed recording is a dead end: the
              file cannot be uploaded again either, because CAP-3 recognises it. */}
          {(kind === "recording" || kind === "meeting") &&
            (run === null || run.status === "failed" || run.status === "cancelled") && (
            <button
              type="button"
              onClick={() => void start()}
              disabled={starting}
              className="flex shrink-0 items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] bg-[var(--primary)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
            >
              {run === null ? <Play size={14} strokeWidth={2} /> : <RotateCcw size={14} strokeWidth={2} />}
              {starting ? "Starting…" : run === null ? "Start processing" : "Run it again"}
            </button>
          )}

          {/* SPCH-9. Only while there is something to stop — a finished run has nothing,
              and a cancelled one that is still finishing its last step cannot be
              cancelled twice. */}
          {(run?.status === "queued" || run?.status === "processing") && (
            <button
              type="button"
              onClick={() => void cancel()}
              disabled={stopping}
              className="flex shrink-0 items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-[var(--border-default)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)] disabled:opacity-50"
            >
              <Square size={12} strokeWidth={2.5} />
              {stopping ? "Stopping…" : "Cancel"}
            </button>
          )}

          {/* A more targeted alternative to "Run it again", next to it: a stopped run
              that only lost its footing partway through (SPCH-10) does not have to redo
              the steps that already succeeded. Not offered while a run is still in
              flight — every option but the one already running would be refused, since
              a step's predecessors have to be done before it can be restarted at. */}
          {run && (run.status === "failed" || run.status === "cancelled") && run.steps.length > 0 && (
            <RestartControl
              run={run}
              sourceId={sourceId}
              state={state}
              onChanged={() => {
                refreshRecordings();
                refreshSetup();
              }}
            />
          )}
        </div>
      </div>

      {problem && (
        <p role="alert" className="mt-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {run && (
        <ol className="mt-[var(--space-4)] flex flex-col gap-[var(--space-2)]">
          {run.steps.map((step) => (
            <StepRow key={step.id} step={step} recordingId={sourceId} expandable={developerMode} />
          ))}
        </ol>
      )}

      {run?.steps.some((step) => step.error) && (
        <p className="mt-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--warning)]">
          {run.steps.find((step) => step.error)?.error}
        </p>
      )}

      <p className="mt-[var(--space-4)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
        The words, their timings, who was speaking, the two put together, a fingerprint of each
        voice, a match against people already known, and finally an entity-aware pass that corrects
        misheard names. Where each one runs is Settings → Engines &amp; models — on the worker's GPU,
        or in the cloud, in which case the Server makes the call and the PC can be off. Insight
        extraction joins the chain in M5.
      </p>
    </section>
  );
}

/**
 * One step of the run.
 *
 * `expandable` is developer mode (ARCH-8): everyone sees the step's name, elapsed time,
 * model and status, because none of that is a debugging concern — it is what "the
 * pipeline is doing something" actually means. Opening a step for its epoch, its lease
 * and its stored artifacts is the part that is a debugging concern, and stays behind the
 * same switch as the recording's own Debug tab, using the same fetch-on-open behaviour
 * and the same facts.
 */
function StepRow({
  step,
  recordingId,
  expandable,
}: {
  step: PipelineStep;
  recordingId: string;
  expandable: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { artifacts, problem } = useStepArtifacts(recordingId, step.id, expandable && open);

  // Live while the step is still running: a claimed or running step has a `startedAt`
  // and no `completedAt` yet, so "so far" is measured against now rather than left
  // blank until it finishes. The panel's own poll (every 2s while the run is in
  // flight) is what keeps this moving — there is no separate clock in here.
  const elapsed = step.startedAt
    ? (step.completedAt ? Date.parse(step.completedAt) : Date.now()) - Date.parse(step.startedAt)
    : null;

  const summary = (
    <>
      {expandable && (
        <ChevronRight
          size={14}
          strokeWidth={2}
          aria-hidden
          className={cn("shrink-0 text-[var(--text-muted)] transition-transform", open && "rotate-90")}
        />
      )}

      <span className="min-w-0 flex-1 truncate font-mono text-[length:var(--text-sm)] text-[var(--text-primary)]">
        {step.stepName}
      </span>

      {elapsed !== null && elapsed >= 0 && (
        <span className="shrink-0 text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {formatElapsed(elapsed)}
        </span>
      )}

      {step.model && (
        <span className="max-w-[160px] shrink-0 truncate font-mono text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {step.model}
        </span>
      )}

      {step.attempts > 1 && (
        <span className="shrink-0 text-[length:var(--text-xs)] text-[var(--text-muted)]">
          attempt {step.attempts}
        </span>
      )}

      <span
        className={cn(
          "shrink-0 rounded-[var(--radius-full)] px-[var(--space-2)] py-[2px] text-[length:var(--text-xs)] font-semibold",
          step.status === "failed"
            ? "bg-[var(--surface-2)] text-[var(--warning)]"
            : "bg-[var(--surface-2)] text-[var(--text-secondary)]",
        )}
      >
        {STEP_LABEL[step.status] ?? step.status}
      </span>
    </>
  );

  return (
    <li className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)]">
      {expandable ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          className="flex w-full items-center gap-[var(--space-3)] px-[var(--space-3)] py-[var(--space-2)] text-left"
        >
          {summary}
        </button>
      ) : (
        <div className="flex items-center gap-[var(--space-3)] px-[var(--space-3)] py-[var(--space-2)]">
          {summary}
        </div>
      )}

      {expandable && open && <StepDetails step={step} artifacts={artifacts} problem={problem} />}
    </li>
  );
}

const STEP_LABEL: Record<PipelineStep["status"], string> = {
  pending: "Waiting",
  claimed: "Picked up",
  running: "Running",
  done: "Done",
  failed: "Failed",
  cancelled: "Cancelled",
};

function Describe(run: PipelineRun | null, kind: string): string {
  if (run === null) {
    if (kind === "recording") {
      return "This recording has never been through the pipeline. It was uploaded before there was one.";
    }
    if (kind === "meeting") {
      return "This meeting hasn't been processed yet — start it to correct the transcript, summarize it, and extract insights.";
    }
    return `Nothing processes a ${kind} yet.`;
  }

  // SPCH-9's in-between. Cancellation is observed *between* steps, so there is always a
  // stretch where the run has stopped and the work has not — and saying "Cancelled"
  // through it would have somebody watching a step they had been told was over.
  if (run.cancelling) return "Cancelled — finishing the step that was already running.";

  switch (run.status) {
    case "queued":
      return "Waiting for a worker to pick it up.";
    case "processing":
      return "A worker is running it now.";
    case "done":
      return "Finished.";
    case "failed":
      return "Stopped on a failure.";
    default:
      return "Cancelled. Nothing after the step that was running was started.";
  }
}
